import { Button, Modal } from "@modelve/ui";
import type { ApiResourceRoute } from "api/resources";
import { useApiDeleteAsync } from "api/hooks";
import { ClientType } from "api/routing";
import { createContext, ReactNode, useContext, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

type DeleteStatus = "deleted" | "dismissed";

export interface DeleteRequest {
  title: string;
  message?: string;
  route: ApiResourceRoute;
  clientType: ClientType;
  onCompleted: (status: DeleteStatus) => void;
  onError: (error: unknown) => void;
}

export interface DeleteModalContextState {
  setRequest: (request: DeleteRequest | undefined) => void;
}

const DeleteModalContext = createContext<DeleteModalContextState>({
  setRequest: () => {},
});

export const DeleteModelProvider = ({ children }: { children: ReactNode }) => {
  const [request, setRequest] = useState<DeleteRequest | undefined>();

  const context = useMemo<DeleteModalContextState>(
    () => ({
      setRequest,
    }),
    [setRequest],
  );

  return (
    <DeleteModalContext.Provider value={context}>
      <DeleteModal request={request} setRequest={setRequest} />
      {children}
    </DeleteModalContext.Provider>
  );
};

const useDeleteModalContext = () =>
  useContext<DeleteModalContextState>(DeleteModalContext);

interface DeleteModelProps extends DeleteModalContextState {
  request?: DeleteRequest;
}

export const DeleteModal = ({ request, setRequest }: DeleteModelProps) => {
  const { t } = useTranslation();

  const { isLoading, onRequest } = useApiDeleteAsync();

  const onDelete = async () => {
    if (!request) return;
    try {
      await onRequest(request.route);
      request.onCompleted("deleted");
    } catch (error) {
      request.onError(error);
    } finally {
      setRequest(undefined);
    }
  };

  const onDismiss = () => {
    request?.onCompleted("dismissed");
    setRequest(undefined);
  };

  return (
    <Modal.Data data={request}>
      {(request) => (
        <>
          <Modal.Header>
            <Modal.Title>{request?.title}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {request?.message ??
              t(
                "common.message.confirm_delete",
                "This action is final and can not be reversed.",
              )}
          </Modal.Body>
          <Modal.Footer className="flex flex-row justify-between">
            <Button
              disabled={isLoading}
              variant="danger"
              onClick={onDelete}
              size="sm"
            >
              {t("common.button.delete", "Delete")}
            </Button>
            <Button disabled={isLoading} onClick={onDismiss} size="sm">
              {t("common.button.cancel", "Cancel")}
            </Button>
          </Modal.Footer>
        </>
      )}
    </Modal.Data>
  );
};

export const useDeleteModal = () => {
  const { setRequest } = useDeleteModalContext();

  const toggle = (
    title: string,
    route: ApiResourceRoute,
    clientType = ClientType.Base,
    message?: string,
  ) =>
    new Promise<DeleteStatus>((res, rej) => {
      setRequest({
        title,
        route,
        clientType,
        message,
        onCompleted: res,
        onError: rej,
      });
    });

  return {
    toggle,
  };
};
